import { Component, inject, Input, OnInit, signal, computed } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ProductsService } from '../../../services/products.service';
import { Product } from '../../../shared/interfaces';
import { BaseAuthComponent } from '../../../shared/common/BaseAuthComponent.class';

@Component({
  selector: 'app-product-add',
  imports: [CommonModule, ReactiveFormsModule, DecimalPipe],
  templateUrl: './product-add.html',
  styleUrl: './product-add.css',
})
export class ProductAdd extends BaseAuthComponent implements OnInit {
  productForm: FormGroup;
  @Input() entityId: string | null = null;
  productsService = inject(ProductsService);
  isLoadingData = signal<boolean>(false);
  private priceFormatter = new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
  });

  private currencyFormatter = new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  // Computed margin
  private costValue = signal(0);
  private priceValue = signal(0);

  profitMargin = computed(() => {
    const cost = this.costValue();
    const price = this.priceValue();
    if (cost <= 0 || price <= 0) return null;
    return ((price - cost) / cost) * 100;
  });

  profitAmount = computed(() => {
    return this.priceValue() - this.costValue();
  });

  /** Share of the sale price that covers the cost (0–100), for the split bar */
  costShare = computed(() => {
    const price = this.priceValue();
    if (price <= 0) return 0;
    return Math.min(100, (this.costValue() / price) * 100);
  });

  constructor(private fb: FormBuilder) {
    super();
    this.productForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      sku: [''],
      category: [''],
      cost: ['', [Validators.required, Validators.min(0)]],
      price: ['', [Validators.required, Validators.min(0)]],
      stock: ['', [Validators.required, Validators.min(0)]],
      isActive: [true],
    });
  }

  ngOnInit(): void {
    if (this.entityId) {
      this.loadEntity(this.entityId);
    }
  }

  loadEntity(productId: string) {
    this.isLoadingData.set(true);
    this.productsService.getProduct(productId).subscribe({
      next: (res) => {
        this.mapProductToForm(res);
        this.isLoadingData.set(false);
      },
      error: (err) => {
        console.error(err);
        this.isLoadingData.set(false);
      },
    });
  }

  private mapProductToForm(product: Product) {
    this.productForm.patchValue({
      name: product.name,
      description: product.description || '',
      sku: product.sku || '',
      category: product.category || '',
      cost: this.priceFormatter.format(product.cost),
      price: this.priceFormatter.format(product.price),
      stock: product.stock,
      isActive: product.isActive,
    });
    this.costValue.set(product.cost);
    this.priceValue.set(product.price);
  }

  onCostInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    const numValue = digits ? Number(digits) : 0;
    const formatted = digits ? this.priceFormatter.format(numValue) : '';
    input.value = formatted;
    (this.productForm.get('cost') as FormControl).setValue(formatted, {
      emitEvent: false,
    });
    this.costValue.set(numValue);
  }

  onPriceInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '');
    const numValue = digits ? Number(digits) : 0;
    const formatted = digits ? this.priceFormatter.format(numValue) : '';
    input.value = formatted;
    (this.productForm.get('price') as FormControl).setValue(formatted, {
      emitEvent: false,
    });
    this.priceValue.set(numValue);
  }

  generateSku() {
    const name = this.productForm.get('name')?.value || '';
    const prefix = name
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 3)
      .map((w: string) => w.substring(0, 3).toUpperCase())
      .join('-');
    const suffix = String(Math.floor(Math.random() * 900) + 100);
    this.productForm.patchValue({ sku: prefix ? `${prefix}-${suffix}` : `PRD-${suffix}` });
  }

  showError(control: string): boolean {
    const c = this.productForm.get(control);
    return !!(c && c.invalid && c.touched);
  }

  toggleActive() {
    const current = this.productForm.get('isActive')?.value;
    this.productForm.patchValue({ isActive: !current });
  }

  formatCurrency(value: number): string {
    return this.currencyFormatter.format(value);
  }

  isValid(): boolean {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return false;
    }
    return true;
  }

  getValue(): any {
    if (!this.isValid()) {
      return null;
    }

    const formValue = this.productForm.getRawValue();
    return {
      productId: this.entityId || undefined,
      name: formValue.name,
      description: formValue.description || undefined,
      sku: formValue.sku || undefined,
      category: formValue.category || undefined,
      cost: this.parsePrice(formValue.cost),
      price: this.parsePrice(formValue.price),
      stock: parseInt(formValue.stock, 10) || 0,
      isActive: formValue.isActive !== undefined ? formValue.isActive : true,
    };
  }

  private parsePrice(value: string | number | null): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const digits = value.replace(/\D/g, '');
    return digits ? Number(digits) : 0;
  }
}
